//third-party
import { Row, Col, Form, Button as BsButton, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useLoaderData, useNavigate } from "react-router-dom";

//spotsync
import { Facility } from "./Data/ApiTypes";
import { Input } from "./Controls/Input";
import { MediumPage } from "./Controls/MediumPage";
import { PageTitle } from "./Controls/PageTitle";
import { useParamIds } from "./Data/Common";
import { Button } from "./Controls/Button";
import { MoneyInput } from "./Controls/MoneyInput";
import { useEffect, useState } from "react";
import { MonitorFacilityApiApi, ServerTransferMonitorPlanCreateReq } from "./shared/api/axios-client";

import { Select } from "./Controls/Select";
import { createApiService } from "./shared/apiConfig";
import { toast } from "react-toastify";
import { FEATURES, isFeatureEnabled } from "./Data/Features";
import { generateCouponCode } from "./FacilityPlanPriceAddModal";
import { Calendar, InfoCircleFill } from "react-bootstrap-icons";
import { useAtom } from "jotai";
import { authAtom } from "./Data/Atoms";
import { canPerformAction, extractClaims } from "./helpers/AuthorizationManager";
import {
    lateFeeRulesAreValid,
    PlanLateFeeRuleForm,
    PlanLateFeeRulesSection,
    toApiLateFeeRules,
} from "./PlanLateFeeRulesSection";
import { CollapsibleHtmlField, PlanSectionCard, PlanSectionTabs, ProrateLabel, usePlanSectionNav } from "./PlanFormChrome";

const monitorApi = createApiService(MonitorFacilityApiApi);

export function FacilityPlanCreatePage() {
    const { facilityId } = useParamIds();
    const [loading, setLoading] = useState(false);
    const facility = useLoaderData() as Facility;
    const navigate = useNavigate();
    const [auth] = useAtom(authAtom);
    const canEditPlan = canPerformAction(extractClaims(auth), "edit-plan");

    const [lateFeeRules, setLateFeeRules] = useState<PlanLateFeeRuleForm[]>([]);
    const [clearedLateFeesOnConversionOff, setClearedLateFeesOnConversionOff] = useState(false);
    const [lateFeeShowErrors, setLateFeeShowErrors] = useState(false);
    const [termsExpanded, setTermsExpanded] = useState(false);
    const [approvalExpanded, setApprovalExpanded] = useState(false);

    useEffect(() => {
        if (!canEditPlan) {
            navigate(`/facility/${facilityId}/plan`, { replace: true });
        }
    }, [canEditPlan, facilityId, navigate]);

    const { register, handleSubmit, watch, control, setValue, clearErrors, formState: { errors } } = useForm<ServerTransferMonitorPlanCreateReq>({
        defaultValues: {
            facilityId: facility.facilityId,
            name: "",
            accessHours: "",
            description: "",
            dayOfMonthInvoice: 15,
            dayOfMonthDue: 1,
            initialPrice: 150,
            prorate: "Monthly",
            isOneTermPlan: false,
            isUsageBasedBilling: false,
            renewalFailureDeactivateAfterDays: 0,
            cancellationWindowDays: 0,
        },
    });

    const isRenewalEnabled = watch("renewalFailureDeactivateAfterDays") > 0;
    const isCancellationWindowEnabled = (watch("cancellationWindowDays") ?? 0) > 0;
    const isUsageBasedBilling = !!watch("isUsageBasedBilling");
    const { tabs, activeSection, jumpTo } = usePlanSectionNav(isUsageBasedBilling);

    useEffect(() => {
        if (isUsageBasedBilling) {
            setValue("initialPrice", 0);
            setValue("isPrivate", true);
            setValue("renewalFailureDeactivateAfterDays", 0);
            setValue("cancellationWindowDays", 0);
            setLateFeeRules([]);
            setClearedLateFeesOnConversionOff(false);
            clearErrors("initialPrice");
            clearErrors("accessHours");
            clearErrors("renewalFailureDeactivateAfterDays");
            clearErrors("cancellationWindowDays");
        }
    }, [clearErrors, isUsageBasedBilling, setValue]);

    useEffect(() => {
        if (errors.terms) setTermsExpanded(true);
    }, [errors.terms]);

    const onSubmit = async (data: ServerTransferMonitorPlanCreateReq) => {
        if (!isUsageBasedBilling && isRenewalEnabled && lateFeeRules.length > 0 && !lateFeeRulesAreValid(lateFeeRules)) {
            setLateFeeShowErrors(true);
            jumpTo("payments");
            return;
        }
        setLoading(true);
        if (data.isUsageBasedBilling) {
            data.initialPrice = 0;
            data.isPrivate = true;
        }
        if (!isRenewalEnabled) data.renewalFailureDeactivateAfterDays = 0;
        if (!isCancellationWindowEnabled) data.cancellationWindowDays = 0;
        data.lateFeeRules = isUsageBasedBilling || !isRenewalEnabled ? [] : toApiLateFeeRules(lateFeeRules);
        data.couponCode = generateCouponCode(null);
        monitorApi.createPlan({
            facilityId: facilityId,
            serverTransferMonitorPlanCreateReq: data,
        }).then(() => {
            toast.success("Plan created successfully");
            navigate(`/facility/${facilityId}/plan`);
        }).catch((err) => {
            toast.error(err);
        }).finally(() => {
            setLoading(false);
        });
    };

    return <MediumPage>
        <div className="plan-edit-sticky sticky-top">
            <div className="d-flex align-items-center gap-3">
                <BsButton variant="link" className="p-0" onClick={() => navigate(-1)}>← Back</BsButton>
                <PageTitle className="mb-0">Create Plan</PageTitle>
            </div>
            <PlanSectionTabs tabs={tabs} activeSection={activeSection} onJump={jumpTo} />
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
            <PlanSectionCard
                id="details"
                title="Details"
                description="Name, description, and plan type. Type flags cannot be changed after saving."
            >
                <Input
                    type="text"
                    label="Name"
                    {...register("name", { required: "Name is required" })}
                    error={errors.name}
                />
                <Input
                    type="text"
                    label="Description"
                    as="textarea"
                    rows={2}
                    maxLength={256}
                    {...register("description", { required: "Description is required" })}
                    error={errors.description}
                />

                {!isUsageBasedBilling && (
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="switch"
                            id="isPrivate"
                            label={<>
                                Private Plan
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id="private-plan-tooltip">Private plans are only visible to staff members and can be used to assign plans for invited parkers only. These are not visible from the public search results. Important: This cannot be changed after saving.</Tooltip>}
                                >
                                    <InfoCircleFill className="icon-shift-up" size={14} style={{ cursor: "help", marginLeft: "4px" }} />
                                </OverlayTrigger>
                            </>}
                            {...register("isPrivate")}
                        />
                    </Form.Group>
                )}

                {isFeatureEnabled(facility, FEATURES.ONE_TERM_PARKING) && (
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="switch"
                            id="isOneTermPlan"
                            label={<>
                                One Term Plan (plan will not auto-renew)
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id="one-term-plan-tooltip">One-term plans expire after a single billing period. Important: This cannot be changed after saving.</Tooltip>}
                                >
                                    <InfoCircleFill className="icon-shift-up" size={14} style={{ cursor: "help", marginLeft: "4px" }} />
                                </OverlayTrigger>
                            </>}
                            {...register("isOneTermPlan")}
                        />
                    </Form.Group>
                )}

                {isFeatureEnabled(facility, FEATURES.USAGE_BASED_PLANS) && (
                    <Form.Group className="mb-0">
                        <Form.Check
                            type="switch"
                            id="isUsageBasedBilling"
                            label={<>
                                Usage-Based Billing
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id="usage-based-billing-tooltip">Usage-based plans can be billed retroactively based on usage. These plans can only be enrolled in with an invitation. Important: This cannot be changed after saving.</Tooltip>}
                                >
                                    <InfoCircleFill className="icon-shift-up" size={14} style={{ cursor: "help", marginLeft: "4px" }} />
                                </OverlayTrigger>
                            </>}
                            {...register("isUsageBasedBilling")}
                        />
                    </Form.Group>
                )}
            </PlanSectionCard>

            <PlanSectionCard
                id="content"
                title="Content"
                description={isUsageBasedBilling
                    ? "Legal terms parkers agree to."
                    : "Legal terms and the message sent when a subscription is approved."}
            >
                <Controller
                    name="terms"
                    control={control}
                    rules={{ required: "Terms and Conditions is required" }}
                    render={({ field }) => (
                        <CollapsibleHtmlField
                            label="Terms and Conditions"
                            value={field.value || ""}
                            onChange={field.onChange}
                            expanded={termsExpanded}
                            onToggle={() => setTermsExpanded((v) => !v)}
                            placeholder="Enter terms and conditions..."
                            error={errors.terms?.message}
                            canEdit
                        />
                    )}
                />
                {!isUsageBasedBilling && (
                    <Controller
                        name="approvalEmailText"
                        control={control}
                        render={({ field }) => (
                            <CollapsibleHtmlField
                                label="Approval Email Text"
                                hint="Optional message that appears in the email parkers receive when their subscription is approved."
                                value={field.value || ""}
                                onChange={field.onChange}
                                expanded={approvalExpanded}
                                onToggle={() => setApprovalExpanded((v) => !v)}
                                placeholder="Enter the approval email text..."
                                canEdit
                            />
                        )}
                    />
                )}
            </PlanSectionCard>

            {!isUsageBasedBilling && (
            <PlanSectionCard
                id="scheduling"
                title="Scheduling & price"
                description="When invoices go out, how proration works, access hours, and the listed price."
            >
                <Row>
                    <Col xs={12} md={4}>
                        <Input
                            type="number"
                            label="Invoice Day of Month"
                            icon={<Calendar />}
                            explanation="Invoices are generated on this day of the month (10–20)"
                            required
                            min={10}
                            max={20}
                            {...register("dayOfMonthInvoice", {
                                valueAsNumber: true,
                                required: "Invoice day is required",
                                min: { value: 10, message: "Must be between 10 and 20" },
                                max: { value: 20, message: "Must be between 10 and 20" },
                            })}
                            error={errors.dayOfMonthInvoice}
                        />
                    </Col>
                    <Col xs={12} md={4}>
                        <Select
                            label={<ProrateLabel />}
                            required
                            min={1} max={28}
                            {...register("prorate")}
                            error={errors.prorate}
                        >
                            <option value={"Daily"}>Daily</option>
                            <option value={"Monthly"}>1/2 Monthly</option>
                            <option value="">None</option>
                        </Select>
                    </Col>
                    <Col xs={12} md={4}>
                        <Input
                            type="text"
                            label="Access Hours"
                            placeholder="Mon - Fri 7am - 7pm"
                            {...register("accessHours", { required: "Access hours are required" })}
                            error={errors.accessHours}
                        />
                    </Col>
                </Row>
                <input type="hidden" {...register("dayOfMonthDue", { valueAsNumber: true })} />
                <MoneyInput
                    label="Initial Price"
                    explanation="This Plan will be listed with this price. Additional pricing options can be added later from the main Plan edit page."
                    required
                    {...register("initialPrice", {
                        valueAsNumber: true,
                        min: { value: 0.01, message: "Price must be greater than 0" },
                    })}
                    error={errors.initialPrice}
                />
            </PlanSectionCard>
            )}

            {!isUsageBasedBilling && (
            <PlanSectionCard
                id="payments"
                title="Payments & fees"
                description="Failed-autopay conversion timing and late-fee tiers on unpaid invoices. Conversion timing determines when the first tier can fire."
            >
                <div className="d-flex align-items-center gap-2 gap-md-4 flex-wrap">
                    <Form.Check
                        type="switch"
                        id="renewalFailureEnabled"
                        label="Convert to invoiced parker after failed payments"
                        checked={isRenewalEnabled}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setValue("renewalFailureDeactivateAfterDays", 7);
                                setClearedLateFeesOnConversionOff(false);
                            } else {
                                setValue("renewalFailureDeactivateAfterDays", 0);
                                clearErrors("renewalFailureDeactivateAfterDays");
                                if (lateFeeRules.length > 0) {
                                    setLateFeeRules([]);
                                    setClearedLateFeesOnConversionOff(true);
                                }
                            }
                        }}
                    />
                    <Form.Group className="mb-0 d-flex align-items-center gap-2 ps-2 ps-md-3 border-start border-secondary">
                        <Form.Label htmlFor="renewalFailureDays" className="mb-0 text-nowrap">Days</Form.Label>
                        <Form.Control
                            id="renewalFailureDays"
                            type="number"
                            min={1}
                            disabled={!isRenewalEnabled}
                            className="text-end"
                            style={{ width: "4.5rem" }}
                            {...register("renewalFailureDeactivateAfterDays", {
                                valueAsNumber: true,
                                validate: (v) => v === 0 || v >= 4 || "Must be at least 4 day when enabled",
                            })}
                            isInvalid={!!errors.renewalFailureDeactivateAfterDays}
                        />
                    </Form.Group>
                </div>
                {errors.renewalFailureDeactivateAfterDays && (
                    <Form.Text className="d-block text-danger ms-1">
                        {errors.renewalFailureDeactivateAfterDays.message}
                    </Form.Text>
                )}
                <p className="plan-accent-note mb-0 mt-2">
                    {!isRenewalEnabled
                        ? "After 3 failed daily charges, the subscription stays on autopay. Turn this on to convert to invoiced billing after a grace period."
                        : <>
                            After 3 failed daily charges, the subscription converts to invoiced billing{" "}
                            <b>{watch("renewalFailureDeactivateAfterDays")} day(s)</b>{" "}
                            after the billing period starts. The account stays active and receives an invoice for the balance due.
                          </>}
                </p>
                {clearedLateFeesOnConversionOff && !isRenewalEnabled && (
                    <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 mt-2 small" role="alert">
                        <InfoCircleFill className="flex-shrink-0 mt-1" />
                        <span>
                            Late-fee tiers were cleared. Failed autopay will not generate an invoice, so late fees cannot apply.
                        </span>
                    </div>
                )}

                <PlanLateFeeRulesSection
                    conversionEnabled={isRenewalEnabled}
                    conversionDays={watch("renewalFailureDeactivateAfterDays") || 0}
                    rules={lateFeeRules}
                    onChange={setLateFeeRules}
                    embedded
                    showErrors={lateFeeShowErrors}
                />
            </PlanSectionCard>
            )}

            {!isUsageBasedBilling && (
            <PlanSectionCard
                id="cancellation"
                title="Cancellation"
                description="Advance notice parkers must give when they cancel in Connect. Staff cancellations in Monitor are not affected."
            >
                <div className="d-flex align-items-center gap-2 gap-md-4 flex-wrap">
                    <Form.Check
                        type="switch"
                        id="cancellationWindowEnabled"
                        label="Require cancellation notice"
                        checked={isCancellationWindowEnabled}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setValue("cancellationWindowDays", 30);
                            } else {
                                setValue("cancellationWindowDays", 0);
                                clearErrors("cancellationWindowDays");
                            }
                        }}
                    />
                    <Form.Group className="mb-0 d-flex align-items-center gap-2 ps-2 ps-md-3 border-start border-secondary">
                        <Form.Label htmlFor="cancellationWindowDays" className="mb-0 text-nowrap">Days</Form.Label>
                        <Form.Control
                            id="cancellationWindowDays"
                            type="number"
                            min={2}
                            disabled={!isCancellationWindowEnabled}
                            className="text-end"
                            style={{ width: "4.5rem" }}
                            {...register("cancellationWindowDays", {
                                valueAsNumber: true,
                                validate: (v) => v == null || v === 0 || v >= 2 || "Must be at least 2 days when enabled",
                            })}
                            isInvalid={!!errors.cancellationWindowDays}
                        />
                    </Form.Group>
                </div>
                {errors.cancellationWindowDays && (
                    <Form.Text className="d-block text-danger ms-1">
                        {errors.cancellationWindowDays.message}
                    </Form.Text>
                )}
                <p className="plan-accent-note mb-0 mt-2">
                    {!isCancellationWindowEnabled
                        ? "Parkers in Connect can cancel effective immediately. No advance notice required."
                        : <>
                            Parkers in Connect must pick a cancellation date at least{" "}
                            <b>{watch("cancellationWindowDays")} day(s)</b>{" "}
                            from today. Staff cancellations in Monitor are not affected.
                          </>}
                </p>
            </PlanSectionCard>
            )}

            <div className="d-flex justify-content-end gap-2 mb-3">
                <Button className="w-50" loading={loading} type="button" onClick={() => navigate(-1)}>
                    Cancel
                </Button>
                <Button className="w-50 btn-main" loading={loading} type="submit">
                    Save Changes
                </Button>
            </div>
        </form>
    </MediumPage>;
}
